import React, { useEffect, useState } from 'react'
import { Box } from 'components'

interface FeeBreakdownProps {
  prizePool: string
  prizePositions: { position?: string; prize?: string; percentage: string }[]
  maxPlayerSize?: string
  playersPerTeam?: number
  paymentMethod: string
  contributors?: any[]
}

interface FeeDetails {
  prizePool: number
  fees: {
    paypalReceiving: {
      amount: number
      description: string
    }
    paypalPayouts: {
      amount: number
      description: string
      playerCount: number
    }
    platformService: {
      amount: number
      percentage: number
      description: string
    }
  }
  totalFees: number
  totalAmount: number
}

export const TournamentFeeBreakdown: React.FC<FeeBreakdownProps> = ({
  prizePool,
  prizePositions,
  maxPlayerSize,
  playersPerTeam,
  paymentMethod,
  contributors,
}) => {
  const [feeBreakdown, setFeeBreakdown] = useState<FeeDetails | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Only calculate fees for PayPal tournaments
    if (paymentMethod !== 'paypal') {
      setFeeBreakdown(null)
      return
    }

    const calculateFees = async () => {
      const prizePoolNum = parseFloat(prizePool || '0')

      // Don't calculate if prize pool is 0 or invalid
      if (prizePoolNum <= 0) {
        setFeeBreakdown(null)
        return
      }

      // Don't calculate if no prize positions
      if (!prizePositions || prizePositions.length === 0) {
        setFeeBreakdown(null)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
        const response = await fetch(`${API_BASE_URL}/tournament/payment/calculate-fees`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            prizePool: prizePoolNum,
            prizePositions: prizePositions,
            maxPlayerSize: maxPlayerSize ? parseInt(maxPlayerSize) : playersPerTeam || 5,
            contributors: contributors || [],
          }),
        })

        const data = await response.json()

        if (data.success) {
          setFeeBreakdown(data.feeBreakdown)
        } else {
          setError(data.error || 'Failed to calculate fees')
        }
      } catch (err: any) {
        console.error('Error calculating fees:', err)
        setError('Failed to calculate fees')
      } finally {
        setLoading(false)
      }
    }

    // Debounce the calculation to avoid too many API calls
    const timeoutId = setTimeout(() => {
      calculateFees()
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [prizePool, prizePositions, maxPlayerSize, playersPerTeam, paymentMethod, contributors])

  // Don't show anything for non-PayPal tournaments
  if (paymentMethod !== 'paypal') {
    return null
  }

  // Don't show if no prize pool
  if (!prizePool || parseFloat(prizePool) <= 0) {
    return null
  }

  return (
    <Box col className="bg-light-container-primary dark:bg-dark-container-primary p-4 rounded-lg border border-light-stroke-primary dark:border-dark-stroke-primary">
      <div className="text-lg font-bold mb-3 text-light-typo-primary dark:text-dark-typo-primary">
        💰 Payment Breakdown
      </div>

      {loading && (
        <div className="text-sm text-light-typo-secondary dark:text-dark-typo-secondary">
          Calculating fees...
        </div>
      )}

      {error && (
        <div className="text-sm text-red-500">
          {error}
        </div>
      )}

      {!loading && !error && feeBreakdown && (
        <div className="space-y-2">
          {/* Prize Pool */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-light-typo-secondary dark:text-dark-typo-secondary">
              Prize Pool
            </span>
            <span className="text-sm font-semibold text-light-typo-primary dark:text-dark-typo-primary">
              ${feeBreakdown.prizePool.toFixed(2)}
            </span>
          </div>

          <hr className="border-light-stroke-primary dark:border-dark-stroke-primary" />

          {/* Fees Section */}
          <div className="text-sm font-bold text-light-typo-primary dark:text-dark-typo-primary mt-2">
            Fees:
          </div>

          {/* PayPal Receiving Fee */}
          <div className="flex justify-between items-start pl-3">
            <div className="flex-1">
              <div className="text-sm text-light-typo-secondary dark:text-dark-typo-secondary">
                {feeBreakdown.fees.paypalReceiving.description}
              </div>
            </div>
            <span className="text-sm font-medium text-light-typo-primary dark:text-dark-typo-primary ml-2">
              ${feeBreakdown.fees.paypalReceiving.amount.toFixed(2)}
            </span>
          </div>

          {/* PayPal Payout Fees */}
          <div className="flex justify-between items-start pl-3">
            <div className="flex-1">
              <div className="text-sm text-light-typo-secondary dark:text-dark-typo-secondary">
                {feeBreakdown.fees.paypalPayouts.description}
              </div>
              <div className="text-xs text-light-typo-tertiary dark:text-dark-typo-tertiary mt-0.5">
                {feeBreakdown.fees.paypalPayouts.playerCount} players across winning teams
              </div>
            </div>
            <span className="text-sm font-medium text-light-typo-primary dark:text-dark-typo-primary ml-2">
              ${feeBreakdown.fees.paypalPayouts.amount.toFixed(2)}
            </span>
          </div>

          {/* Platform Service Fee */}
          <div className="flex justify-between items-start pl-3">
            <div className="flex-1">
              <div className="text-sm text-light-typo-secondary dark:text-dark-typo-secondary">
                {feeBreakdown.fees.platformService.description}
              </div>
              <div className="text-xs text-light-typo-tertiary dark:text-dark-typo-tertiary mt-0.5">
                For automated tournament management & payouts
              </div>
            </div>
            <span className="text-sm font-medium text-light-typo-primary dark:text-dark-typo-primary ml-2">
              ${feeBreakdown.fees.platformService.amount.toFixed(2)}
            </span>
          </div>

          <hr className="border-light-stroke-primary dark:border-dark-stroke-primary" />

          {/* Total Fees */}
          <div className="flex justify-between items-center">
            <span className="text-sm font-bold text-light-typo-secondary dark:text-dark-typo-secondary">
              Total Fees
            </span>
            <span className="text-sm font-bold text-light-typo-primary dark:text-dark-typo-primary">
              ${feeBreakdown.totalFees.toFixed(2)}
            </span>
          </div>

          <hr className="border-light-stroke-primary dark:border-dark-stroke-primary border-2" />

          {/* Total Amount to Pay */}
          <div className="flex justify-between items-center bg-gradient-primary-dark p-2 rounded">
            <span className="text-base font-bold text-white">
              Total Amount to Pay
            </span>
            <span className="text-lg font-bold text-white">
              ${feeBreakdown.totalAmount.toFixed(2)}
            </span>
          </div>

          {/* Info Message */}
          <div className="text-xs text-light-typo-tertiary dark:text-dark-typo-tertiary mt-2 italic">
            💡 You'll be redirected to PayPal to complete this payment after creating the tournament.
          </div>
        </div>
      )}
    </Box>
  )
}
